import { useContext } from 'react';

import { MultiSelect, Select } from '@mantine/core';

import {
  EXTERNAL_ID_MAPPING,
  GRANDPARENT_UUIDS,
  MAIN_REQUIRED_TEXT,
  UUID_EXTERNAL_ID,
  UNMAPPED_TEXT,
  POS_ID_NOT_REQUIRED,
  MAIN_ITEMS,
  IGNORE,
  AS_INSTRUCTION,
  POST_TAG,
  formatMissingPosId,
} from 'constants/mapping';
import { CAMEL_TYPES } from 'constants/menuManagement';

import { formatPrice } from 'helpers/mapping';

import { MenuMappingContext } from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMapping';

interface PosItem {
  uuidName: string;
  price: number;
  uuidExternalId: string | null;
  [key: string]: unknown;
}

interface PosMenuData {
  [type: string]: { [id: string]: PosItem };
}

interface AppMenuData {
  [type: string]: { [key: string]: Record<string, unknown> };
}

interface MenuMappingCtx {
  posMenu: PosMenuData | null;
  appMenu: AppMenuData;
  setEditedItems: (items: unknown) => void;
  checkedItems: string[];
  editedItems: unknown;
  setAppMenu: (menu: AppMenuData) => void;
}

// When an item has a stored POS ID that can't be resolved in the current POS menu, surface
// the "Mapped POS ID does not exist" state instead of the generic unmapped placeholder.
export const getSelectorPlaceholder = (
  selectedValue: string | string[] | undefined,
  posMenu: Record<string, Record<string, unknown>> | null | undefined,
  selectorItemType: string,
) => {
  if (
    typeof selectedValue === 'string' &&
    selectedValue &&
    !posMenu?.[selectorItemType]?.[selectedValue]
  ) {
    return formatMissingPosId(selectedValue);
  }
  return UNMAPPED_TEXT;
};

type ItemSelectorProps = {
  rowKey: string;
  selectedValue: string | string[] | undefined;
  setSelectedValue: (params: unknown) => void;
  selectorItemType: string;
  itemType: string;
  disabled?: boolean;
  allowSelection?: boolean;
  isDefaultModifier?: boolean;
};

export default function ItemSelector({
  rowKey,
  selectedValue,
  setSelectedValue,
  selectorItemType,
  itemType,
  disabled = false,
  allowSelection = false,
  isDefaultModifier = false,
}: ItemSelectorProps) {
  const { posMenu, appMenu, setEditedItems, checkedItems, editedItems, setAppMenu } = useContext(
    MenuMappingContext,
  ) as unknown as MenuMappingCtx;

  const buildPosItemName = (posId: string) => {
    const posItem = posMenu?.[selectorItemType]?.[posId];
    if (!posItem) return '';
    return `${posItem.uuidName} ${formatPrice(posItem.price)} [${posId}]`;
  };

  const checkIfNotRequired = (key: string) =>
    !!(appMenu[itemType][key]?.[IGNORE] || appMenu[itemType][key]?.[AS_INSTRUCTION]);

  const checkIfDisabled = (key: string) => {
    /* only applies to the modifier table,
we disable the selection unless ALL the parent main items are mapped
unless the override is set to true */
    // only modifiers
    if (itemType !== CAMEL_TYPES.modifiers || allowSelection) return false;
    // ignore for default modifier selector (special case), just needs POS item to be selected
    if (isDefaultModifier) return !!appMenu[CAMEL_TYPES.items][UUID_EXTERNAL_ID];
    const grandparentUuids = appMenu[CAMEL_TYPES.modifiers][key]?.[GRANDPARENT_UUIDS];

    if (!grandparentUuids) return true;
    return (grandparentUuids as string[]).reduce((accumulator: boolean, mainItemUuid: string) => {
      if (accumulator) return true;
      const parentMain =
        appMenu[CAMEL_TYPES.items][mainItemUuid] ?? appMenu[CAMEL_TYPES.modifiers][mainItemUuid];
      if (!parentMain?.[UUID_EXTERNAL_ID] && !parentMain?.[EXTERNAL_ID_MAPPING]) return true;
      return accumulator;
    }, false);
  };

  const formatPosItem = (posId: string) => {
    if (checkIfNotRequired(rowKey)) return POS_ID_NOT_REQUIRED;
    if (!posId && checkIfDisabled(rowKey)) return MAIN_REQUIRED_TEXT;
    // eslint-disable-next-line eqeqeq
    if (!posId || (Array.isArray(posId) && posId.length === 0)) return UNMAPPED_TEXT;
    const posItem = posMenu?.[selectorItemType]?.[posId];
    if (!posItem) return formatMissingPosId(posId);
    // we show different indicator symbols based on the POS ID,
    // if it is used multiple times, unused, used once
    const chooseIcon = (externalId: string) => {
      const count = Object.values(appMenu[itemType]).filter(item =>
        (item as PosItem)?.uuidExternalId?.includes(externalId),
      ).length;
      if (count > 1) return POST_TAG.MULTIPLE;
      if (count === 0) return POST_TAG.UNMAPPED;
      return POST_TAG.MAPPED;
    };
    return `${buildPosItemName(posId)} ${chooseIcon(posId)}`;
  };

  const filterModChoicesBasedOnMain = (grandparentUuids: string[]) => {
    const mainItemPosIdsSet = new Set(
      grandparentUuids?.map(
        (mainItemUuid: string) => appMenu[CAMEL_TYPES.items][mainItemUuid]?.[UUID_EXTERNAL_ID],
      ),
    );
    const filteredModifiers = Object.values(posMenu?.[CAMEL_TYPES.modifiers] ?? {}).filter(
      modifier => {
        const mainItems = modifier[MAIN_ITEMS] as string[] | undefined;
        if (!mainItems?.length) return true;
        return mainItems.some((item: string) => mainItemPosIdsSet.has(item));
      },
    );
    return filteredModifiers?.map(item => ({
      value: (item?.[UUID_EXTERNAL_ID] as string) || '',
      label: formatPosItem(item?.[UUID_EXTERNAL_ID] as string),
    }));
  };

  const needsModifiers = isDefaultModifier || selectorItemType === CAMEL_TYPES.modifiers;
  let posItemOptions: { value: string; label: string }[] = [];
  if (
    posMenu &&
    posMenu[CAMEL_TYPES.items] &&
    (!needsModifiers || posMenu[CAMEL_TYPES.modifiers]) &&
    appMenu[itemType]
  ) {
    if (isDefaultModifier) {
      posItemOptions = filterModChoicesBasedOnMain([rowKey]);
    } else if (selectorItemType === CAMEL_TYPES.modifiers && !allowSelection) {
      const rowMainItemParents = appMenu[CAMEL_TYPES.modifiers][rowKey]?.[
        GRANDPARENT_UUIDS
      ] as string[];
      posItemOptions = filterModChoicesBasedOnMain(rowMainItemParents);
    } else {
      const options = Object.values(posMenu[selectorItemType])?.map((item: PosItem) => {
        // case where there are multiple "Unmapped" labels, show the one with the empty string value
        if (item?.uuidExternalId === null) return;
        return {
          value: item.uuidExternalId,
          label: formatPosItem(item.uuidExternalId),
        };
      });
      posItemOptions = options.filter(option => option !== undefined);
    }
  }

  if (isDefaultModifier) {
    return (
      <MultiSelect
        miw="384"
        w="100%"
        placeholder="Select modifiers"
        data={posItemOptions}
        value={selectedValue as string[] | undefined}
        onChange={value => {
          setSelectedValue({
            rowKey,
            value,
            appMenu,
            itemType,
            checkedItems,
            setEditedItems,
            editedItems,
            setAppMenu,
          });
        }}
        disabled={checkIfDisabled(rowKey) || disabled}
        searchable
      />
    );
  }

  return (
    <Select
      miw="384"
      w="100%"
      placeholder={getSelectorPlaceholder(selectedValue, posMenu, selectorItemType)}
      data={posItemOptions}
      value={(typeof selectedValue === 'string' ? selectedValue : '') || ''}
      onChange={value => {
        setSelectedValue({
          rowKey,
          value,
          appMenu,
          itemType,
          checkedItems,
          setEditedItems,
          editedItems,
          setAppMenu,
        });
      }}
      disabled={checkIfDisabled(rowKey) || disabled}
      searchable
    />
  );
}
